/**
 * WsReconnectCoordinator
 *
 * Prevents the "reconnect storm" that causes the page to freeze after a tab
 * has been hidden for a long time.
 *
 * Background:
 *   y-websocket's internal reconnect loop resets its exponential backoff on
 *   every successful WebSocket.onopen event — even if the server immediately
 *   closes the handshake (stale session, rate-limited room, etc.). When a tab
 *   is hidden, the browser aggressively throttles timers so reconnect attempts
 *   silently queue up. When the tab becomes visible again, every queued retry
 *   fires near-simultaneously, hammering the backend at ~100ms intervals and
 *   flooding the console with WebSocket errors — often locking the main thread.
 *
 * Strategy:
 *   1. Hidden > HIDDEN_PAUSE_THRESHOLD_MS → call provider.disconnect() on every
 *      registered provider that is currently connected or connecting. This
 *      tears down y-websocket's internal retry timer, guaranteeing no queued
 *      reconnects can accumulate.
 *   2. Visible again (after we paused) → after a short settle delay, call
 *      provider.connect() one at a time, staggered by RECONNECT_STAGGER_MS,
 *      so the backend never sees N simultaneous opens from a single client.
 *   3. Providers disconnected by their owner (not by us) are never reconnected
 *      — we track our own pauses in a WeakSet.
 *
 * Registration is piggy-backed onto wsTokenRefresher.register/unregister so
 * the common case requires zero call-site changes.
 */
import type { WebsocketProvider } from 'y-websocket';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('WS_RECONNECT_COORD', { enabled: true });

// Pause providers once the tab has been hidden for this long. Short hides
// (tab switches, app switches) stay well under this threshold so normal
// backoff handles them — we only intervene when the backoff loop has had
// enough time to go pathological in a throttled background tab.
const HIDDEN_PAUSE_THRESHOLD_MS = 30_000;

// After the tab becomes visible, wait this long before starting the
// staggered resume. Gives the event loop a moment to breathe after the
// browser un-throttles and prevents racing with any in-flight teardown.
const VISIBILITY_SETTLE_MS = 500;

// Gap between each provider's resume. Prevents thundering-herd opens
// against the WS server when many rooms are active at once.
const RECONNECT_STAGGER_MS = 300;

class WsReconnectCoordinator {
  private readonly providers = new Set<WebsocketProvider>();
  private readonly pausedByUs = new WeakSet<WebsocketProvider>();
  // Per-provider predicates that, when truthy at pause time, keep that provider
  // connected through a long hide. Used by conversation rooms with an in-flight
  // agent run: tearing the socket down mid-run drops live streaming events
  // (the server keeps pushing into a room with clients=0), so the run only
  // becomes visible again on a full reload.
  private readonly exemptionChecks = new Map<WebsocketProvider, () => boolean>();
  private hiddenSince: number | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly resumeTimers = new Set<ReturnType<typeof setTimeout>>();
  private readonly longHideHandlers = new Set<() => void>();

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    }
    if (typeof window !== 'undefined') {
      // Safari/Firefox bfcache restore: visibilitychange may not fire reliably,
      // so pageshow.persisted is the canonical signal to treat as "visible again".
      window.addEventListener('pageshow', (e) => {
        if ((e as PageTransitionEvent).persisted) {
          this.handleVisible();
        }
      });
    }
  }

  register(provider: WebsocketProvider): void {
    this.providers.add(provider);
  }

  unregister(provider: WebsocketProvider): void {
    this.providers.delete(provider);
    this.pausedByUs.delete(provider);
    this.exemptionChecks.delete(provider);
  }

  /**
   * Register a predicate that exempts a provider from the long-hide pause while
   * it returns true. Re-evaluated on every pause, so a provider is only kept
   * alive for as long as the condition holds (e.g. an active agent run). Pass
   * the same provider again to replace the check; call clearPauseExemption to
   * remove it.
   */
  setPauseExemption(provider: WebsocketProvider, check: () => boolean): void {
    this.exemptionChecks.set(provider, check);
  }

  clearPauseExemption(provider: WebsocketProvider): void {
    this.exemptionChecks.delete(provider);
  }

  /**
   * Register a handler to call when the tab has been hidden long enough to trigger
   * a provider pause (i.e. when HIDDEN_PAUSE_THRESHOLD_MS is exceeded). Use this
   * to tear down expensive ephemeral resources (e.g. conversation Y.Docs) that
   * should not keep their in-memory state across a long background interval.
   * Returns an unsubscribe function.
   */
  onLongHide(handler: () => void): () => void {
    this.longHideHandlers.add(handler);
    return () => this.longHideHandlers.delete(handler);
  }

  private onVisibilityChange(): void {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') {
      this.handleHidden();
    } else {
      this.handleVisible();
    }
  }

  private handleHidden(): void {
    if (this.hiddenTimer !== null) return;
    // Cancel any in-flight resume — we're hidden again, so completing a
    // staggered resume would open WS connections in a now-throttled tab.
    this.clearResumeTimers();
    this.hiddenSince = Date.now();
    this.hiddenTimer = setTimeout(() => {
      this.hiddenTimer = null;
      this.pauseAll();
    }, HIDDEN_PAUSE_THRESHOLD_MS);
  }

  private handleVisible(): void {
    if (this.hiddenTimer !== null) {
      clearTimeout(this.hiddenTimer);
      this.hiddenTimer = null;
    }
    const hiddenForMs = this.hiddenSince !== null ? Date.now() - this.hiddenSince : 0;
    this.hiddenSince = null;

    // Only schedule a resume if we actually paused AND no resume is already
    // in flight. Without the second guard, a spurious duplicate 'visible'
    // event would call clearResumeTimers() and cancel an in-flight resume
    // without scheduling a replacement — leaving providers disconnected.
    if (hiddenForMs >= HIDDEN_PAUSE_THRESHOLD_MS && this.resumeTimers.size === 0) {
      const settle = setTimeout(() => {
        this.resumeTimers.delete(settle);
        this.resumeAllStaggered();
      }, VISIBILITY_SETTLE_MS);
      this.resumeTimers.add(settle);
    }
  }

  private pauseAll(): void {
    let paused = 0;
    let exempt = 0;
    // Snapshot the set — a misbehaving provider's disconnect() could
    // synchronously trigger an unregister() via status listeners, and
    // iterating a mutating Set can skip entries.
    for (const provider of Array.from(this.providers)) {
      try {
        const connected = Boolean((provider as { wsconnected?: boolean }).wsconnected);
        const connecting = Boolean((provider as { wsconnecting?: boolean }).wsconnecting);
        if (!connected && !connecting) continue;
        // Keep exempt providers (e.g. conversation rooms with an in-flight run)
        // connected so live streaming events aren't lost to a clients=0 room.
        const check = this.exemptionChecks.get(provider);
        if (check) {
          let isExempt = false;
          try { isExempt = check(); } catch (err) { log.warn('Exemption check threw:', err); }
          if (isExempt) { exempt += 1; continue; }
        }
        provider.disconnect();
        this.pausedByUs.add(provider);
        paused += 1;
      } catch (err) {
        log.warn('Error pausing provider:', err);
      }
    }
    if (paused > 0 || exempt > 0) {
      log.log(`Tab hidden >${HIDDEN_PAUSE_THRESHOLD_MS}ms — paused ${paused} WS provider(s)${exempt > 0 ? `, kept ${exempt} exempt (active run)` : ''}`);
    }

    // Notify long-hide handlers so callers can tear down ephemeral resources
    // (e.g. conversation Y.Docs) whose bloated in-memory state would cause
    // expensive resync payloads on the next reconnect.
    for (const handler of this.longHideHandlers) {
      try { handler(); } catch (err) {
        log.warn('Error in long-hide handler:', err);
      }
    }
  }

  private resumeAllStaggered(): void {
    const toResume: WebsocketProvider[] = [];
    for (const provider of this.providers) {
      if (this.pausedByUs.has(provider)) {
        toResume.push(provider);
      }
    }
    if (toResume.length === 0) return;

    log.log(
      `Tab visible — resuming ${toResume.length} paused WS provider(s) (stagger=${RECONNECT_STAGGER_MS}ms)`
    );

    toResume.forEach((provider, idx) => {
      const timer = setTimeout(() => {
        this.resumeTimers.delete(timer);
        if (!this.providers.has(provider)) return;
        if (!this.pausedByUs.has(provider)) return;
        try {
          provider.connect();
          this.pausedByUs.delete(provider);
        } catch (err) {
          log.warn('Error resuming provider:', err);
        }
      }, idx * RECONNECT_STAGGER_MS);
      this.resumeTimers.add(timer);
    });
  }

  private clearResumeTimers(): void {
    for (const t of this.resumeTimers) clearTimeout(t);
    this.resumeTimers.clear();
  }

  /** Debug helper — snapshot of current coordinator state. */
  public getDebugState(): {
    registeredCount: number;
    exemptionCount: number;
    hiddenSince: number | null;
    hiddenForMs: number;
    pendingPause: boolean;
    pendingResumes: number;
  } {
    return {
      registeredCount: this.providers.size,
      exemptionCount: this.exemptionChecks.size,
      hiddenSince: this.hiddenSince,
      hiddenForMs: this.hiddenSince !== null ? Date.now() - this.hiddenSince : 0,
      pendingPause: this.hiddenTimer !== null,
      pendingResumes: this.resumeTimers.size,
    };
  }
}

export const wsReconnectCoordinator = new WsReconnectCoordinator();
